<script setup>
  import { ref, reactive, onMounted, computed, watch } from 'vue';
import { RefreshCwIcon } from 'lucide-vue-next';

  import BaseCard from "../components/BaseCard.vue";
  import Button from '@/components/Button.vue';
  import ToolPageHeader from "../components/ToolPageHeader.vue";
  import PokemonIcon from '@/components/PokemonIcon.vue';
  import TypeSelect from "../components/TypeSelect.vue"
  import InputGroup from "../components/InputGroup.vue"
  import TypeBadge from '../components/TypeBadge.vue';
  import CopyFormButton from "../components/CopyFormButton.vue";
  import ResultsLine from "../components/typing-analyser/ResultsLine.vue"
  import RatingCard from "../components/typing-analyser/RatingCard.vue"
  import { capitalise, overwriteObject } from '@/utilities';

  import POKEMON_DATA from '../data/pokemon-base-data.json';
  import MULTITYPE_RESISTANCES from '../data/multitype-resistances.json';
  import ALL_RESISTANCES from '../data/all-resistances.json';
  import PERFECT_TRIOS from '../data/perfect-trios.json';
  import PERFECT_TRIOS_STARTERS from '../data/perfect-trios-starters.json';

  const trichotomies = [
    ['diglett', 'dugtrio'],
    ['magnemite', 'magneton'],
  ]

  const buttonFormDefaults = {
    mode: 'single',
    singleType1: 'normal',
    singleType2: '-',
    trioPoke1Type1: 'grass',
    trioPoke1Type2: '-',
    trioPoke2Type1: 'fire',
    trioPoke2Type2: '-',
    trioPoke3Type1: 'water',
    trioPoke3Type2: '-',
  };
  const buttonForm = reactive({
    ...buttonFormDefaults
  });

  const mounted = ref(false);
  onMounted(()=>{
    mounted.value = true;
  })




  const getTypeKey = (type1, type2) => {
    let types = [
      capitalise(type1)
    ];
    if (!['-', ''].includes(type2) && type2 !== type1) {
      types.push(
        capitalise(type2)
      );
    }
    types.sort()
    return types.join(',');
  }




  const mode = ref(buttonFormDefaults.mode);
  const singleTypes = reactive({
    1: buttonFormDefaults.singleType1,
    2: buttonFormDefaults.singleType2
  })
  const triPokeChoice = ref(0);
  const extraTypesCount = ref(0)

  const singleResults = reactive({
    weaknesses: null,
    defenseKeys: null,
    rating: null,
  })

  const trioTypes = reactive({
    1: {
      1: "grass",
      2: "-"
    },
    2: {
      1: "fire",
      2: "-"
    },
    3: {
      1: "water",
      2: "-"
    },
  })

  const trioResults = reactive({
    // cw
    //pokemon 1's type 1 against pokemon2('s typing)
    'p1.t1-p2': 0.5,
    'p1.t2-p2': null,

    'p2.t1-p3': 0.5,
    'p2.t2-p3': null,

    'p3.t1-p1': 0.5,
    'p3.t2-p1': null,

    //acw
    'p1.t1-p3': 2.,
    'p1.t2-p3': null,

    'p3.t1-p2': 2.,
    'p3.t2-p2': null,

    'p2.t1-p1': 2.,
    'p2.t2-p1': null,

    //totals
    'p1-rating': 0,
    'p2-rating': 0,
    'p3-rating': 0,

    // analysis
    'balance': 0,
  })


  const addExtraType = () => {
    extraTypesCount.value += 1
    singleTypes[extraTypesCount.value+2] = null;
  }
  const removeExtraType = (index) => {
    let newTypeList = Object.values(singleTypes)
    newTypeList.splice(index-1, 1)

    let newSingleTypes = {}
    for(var i = 1; i <= Object.values(singleTypes).length; i++){
      newSingleTypes[i+''] = newTypeList[i-1]
    }

    extraTypesCount.value -= 1
    overwriteObject(singleTypes, newSingleTypes)
  }


  onMounted(() => {
    triPokeChoice.value = parseInt(Math.floor(Math.random() * trichotomies.length));
  })

  const triPokeChoiceSingle = computed(() => {
    return Object.values(POKEMON_DATA).find((pokemon)=>{
      return pokemon.basename == trichotomies[triPokeChoice.value][0];
    });
  })
  const triPokeChoiceTrio = computed(() => {
    return Object.values(POKEMON_DATA).find((pokemon)=>{
      return pokemon.basename == trichotomies[triPokeChoice.value][1];
    });
  })






  watch([mounted, singleTypes, extraTypesCount], () => {
    if (!mounted.value){
      return;
    }

    if (extraTypesCount.value){
      let weaknesses = {}
      let rating = '?';
      let defTypeKeys = Object.values(singleTypes).filter(Boolean).filter(x => x != '-')

      for (let attType in ALL_RESISTANCES){
        let amt = 1;
        for (let defType of defTypeKeys){
          amt *= ALL_RESISTANCES[attType][defType]
        }
        if (amt == 1){
          continue;
        }
        if (!weaknesses[amt]){
          weaknesses[amt] = []
        }
        weaknesses[amt].push(attType)
      }

      singleResults.weaknesses = weaknesses;
      singleResults.defenseKeys =  Object.keys(weaknesses).sort();
      singleResults.rating = rating;
      return
    }

    let type_key = getTypeKey(singleTypes[1], singleTypes[2])

    if (!type_key){
      return;
    }
    if (MULTITYPE_RESISTANCES[type_key]){

      let data = MULTITYPE_RESISTANCES[type_key];

      let weaknesses = {}; //key by weakness low to high, values of the types.
      for (var d in data['defences']) {
        let [type, amt] = data['defences'][d];
        if (!weaknesses[amt]) {
          weaknesses[amt] = [];
        }
        weaknesses[amt].push(type.toLowerCase());
      }
      // console.log({weaknesses});

      //translating the range into A through F
      let first_key = Object.keys(MULTITYPE_RESISTANCES)[0];
      let last_key = Object.keys(MULTITYPE_RESISTANCES)[Object.keys(MULTITYPE_RESISTANCES).length - 1];
      let lowest = MULTITYPE_RESISTANCES[first_key].rating;
      let highest = MULTITYPE_RESISTANCES[last_key].rating;

      let letter_mult = (highest - lowest) / 5;

      let i_rating = parseInt(Math.round((data['rating'] - lowest) / letter_mult));
      let rating = "ABCDEFG".substring(i_rating, i_rating+1);


      singleResults.weaknesses = weaknesses;
      singleResults.defenseKeys =  Object.keys(weaknesses).sort();
      singleResults.rating = rating;
    }

  })

  const singleGetPokemonOfType = computed(() => {
    let shortlist = [];
    let count = 0;

    let types = Object.values(singleTypes).filter(Boolean)

    if (types.length && types.length < 3) {
      for (var p in POKEMON_DATA) {
        // console.log(singleTypes[1], "in", POKEMON_DATA[p]['types'], POKEMON_DATA[p]['types'].includes(singleTypes[1]))
        if (
          POKEMON_DATA[p]['types'].includes(types[0]) ) {

          // Specify single type by setting both
          if (types.length > 1 && types[0] == types[1] && POKEMON_DATA[p]['types'].length > 1){
            continue;
          }

          //discard if we have a no-any type2 and it's not there
          if (types[1] != '-' && !POKEMON_DATA[p]['types'].includes(types[1])) {
            continue;
          }

          // //stick to one type only where set
          // if (singleTypes[2] === '-' && POKEMON_DATA[p]['types'].length > 1) {
          //   continue;
          // }

          shortlist.push(POKEMON_DATA[p]);

        }
      }
      count = shortlist.length
      shortlist = shortlist.sort(() => 0.5 - Math.random());
      shortlist = shortlist.slice(0,12)
    }
    return {
      count,
      shortlist
    }
  })




  const setPerfectTrio = ()=>{

    let pick = parseInt(Math.floor( Math.random()*PERFECT_TRIOS.length ));

    trioTypes[1][1] = PERFECT_TRIOS[pick][0][0].toLowerCase();
    trioTypes[1][2] = PERFECT_TRIOS[pick][0].length>1 ? PERFECT_TRIOS[pick][0][1].toLowerCase() : '-';
    trioTypes[2][1] = PERFECT_TRIOS[pick][1][0].toLowerCase();
    trioTypes[2][2] = PERFECT_TRIOS[pick][1].length>1 ? PERFECT_TRIOS[pick][1][1].toLowerCase() : '-';
    trioTypes[3][1] = PERFECT_TRIOS[pick][2][0].toLowerCase();
    trioTypes[3][2] = PERFECT_TRIOS[pick][2].length>1 ? PERFECT_TRIOS[pick][2][1].toLowerCase() : '-';
  }
  const setPerfectTrioStarters = ()=>{
    let pick = parseInt(Math.floor( Math.random()*PERFECT_TRIOS_STARTERS.length ));

    let picks = {};
    let order = ["Grass", "Fire", "Water"];
    for (var p in PERFECT_TRIOS_STARTERS[pick]){
      let types = PERFECT_TRIOS_STARTERS[pick][p];

      for (var i in order){
          let index = types.indexOf(order[i]);
          if (index === 0){
              picks[i] = [types[0], types[1]]
          } else if (index === 1){
              picks[i] = [types[1], types[0]]
          // } else {
          //     console.log('irder[i] not in types', order[i], types)
          }
      }
    }

    trioTypes[1][1] = picks[0][0].toLowerCase();
    trioTypes[1][2] = picks[0][1].toLowerCase();
    trioTypes[2][1] = picks[1][0].toLowerCase();
    trioTypes[2][2] = picks[1][1].toLowerCase();
    trioTypes[3][1] = picks[2][0].toLowerCase();
    trioTypes[3][2] = picks[2][1].toLowerCase();

  }


  watch([
    mounted,
    trioTypes,
  ], ()=>{
    if (!mounted.value){
      return
    }

    for (var p=0; p < 3; p++){
        //setting weakneseses from the other two.
        let pokeIndex = p;
        let cwPokeIndex = (p+1) %3;
        let acwPokeIndex = (p+2) %3;
        pokeIndex += 1;
        cwPokeIndex += 1;
        acwPokeIndex += 1;

        let type_key = getTypeKey(trioTypes[pokeIndex][1], trioTypes[pokeIndex][2])
        let data = MULTITYPE_RESISTANCES[type_key];

        let rating = 0;

        for(var otherpokeIndex of [cwPokeIndex, acwPokeIndex]){
            for (var otherpokeIndext of [1,2]){
                //type from other pokemon
                let other_type = trioTypes[otherpokeIndex][otherpokeIndext];

                if (other_type === "" || (otherpokeIndext === 2 && trioTypes[otherpokeIndex][1] === other_type)){
                    trioResults['p'+otherpokeIndex+'.t'+otherpokeIndext+'-p'+pokeIndex] = null;

                } else {

                    other_type = capitalise(other_type);

                    let weakness = 1.

                    for (var d in data['defences']){
                        let [type, amt] = data['defences'][d];
                        if (type === other_type){
                            weakness = amt;
                            break;
                        }
                    }

                    trioResults['p'+otherpokeIndex+'.t'+otherpokeIndext+'-p'+pokeIndex] = weakness;

                    if (weakness < 1){
                        rating -= (1.- weakness);
                    } else if (weakness > 1){
                        rating += weakness;
                    }

                }


                // console.log(
                //     other_type, '>', type_key,
                //     'p'+otherpokeIndex+'.t'+otherpokeIndext+'-p'+pokeIndex,
                //     new_results['p'+otherpokeIndex+'.t'+otherpokeIndext+'-p'+pokeIndex]
                // )

            }
        }
        trioResults['p'+pokeIndex+'-rating'] = rating;
      }

    trioResults.balance = (
        Math.max(trioResults['p1-rating'], trioResults['p2-rating'], trioResults['p3-rating'])
        - Math.min(trioResults['p1-rating'], trioResults['p2-rating'], trioResults['p3-rating'])
    );

  })


  const trioBalanceClass = ref('');
  const trioBalanceText = ref('Calculating...');

  watch(trioResults, ()=>{
    if (trioResults.balance == 0){
      trioBalanceClass.value = "text-green-500";
      trioBalanceText.value = "Perfect Balance";
    } else if (trioResults.balance < 2){
      trioBalanceClass.value = "text-orange-500";
      trioBalanceText.value = "Moderate Balance";
    } else {
      trioBalanceClass.value = "text-red-600";
      trioBalanceText.value = "Unbalanced";
    }
  })







  // hash form stuff

  const buttonFormLoadSetter = (key, value) => {
    // so this is only used by the copy button when loading the page
    // And rather than setting the buttonForm values, we rely on a watch for that, and set the refs.
    if (key == "mode"){
      mode.value = value;

    } else if (key.startsWith('singleType')){
      let index = parseInt(key.substr('singleType'.length), 10)
      singleTypes[index] = value;

      if (index > 2){
        extraTypesCount.value = Math.max(extraTypesCount.value, index-2)
      }

    } else if (key.startsWith('trioPoke')){
      let regexed = key.match(/trioPoke(\d)Type(\d)/)
      trioTypes[parseInt(regexed[1], 10)][parseInt(regexed[2], 10)] = value;
    }
  }

  watch([
      mode,
      singleTypes,
      trioTypes,
    ],
    ()=>{
      buttonForm.mode = mode.value;

      if (mode.value == "single"){
        Object.keys(singleTypes).forEach(typeIndex => {
          buttonForm['singleType'+typeIndex] = singleTypes[typeIndex]
        })
      } else {
        for(let p = 1; p <= 3; p++){
          for (let t = 1; t <= 2; t++){
            buttonForm[`trioPoke${p}Type${t}`] = trioTypes[p][t];
          }
        }
      }

      for (let key of Object.keys(buttonForm)){
        if (
          (mode.value == "single" && key.startsWith('trioPoke'))
          || (mode.value == 'trio' && key.startsWith('singleType'))
        ){
          delete buttonForm[key]
        }
      }

    }
  )
</script>

<template>
  <div class="container mt-8 mb-20">
    <ToolPageHeader tool="typing-analyser">
      <template #info>
        <p class="my-2">In Single Mode you can see a detailed view of a typing's resistances, and in Trio Mode you can see how
        balanced a trio of types is.</p>
      </template>
      <template #controls>
        <CopyFormButton
          tool="typing-analyser"
          :form="buttonForm"
          :form-defaults="buttonFormDefaults"
          :form-mutator="buttonFormLoadSetter"
          :allow-non-defaults="true"
        />
      </template>
    </ToolPageHeader>

    <div class="my-6 w-full flex flex-col gap-4 lg:flex-row lg:gap-8 clear-both pt-6 justify-center">
      <Button
        tool="typing-analyser"
        :secondary="mode !== 'single'"
        :pokemon="triPokeChoiceSingle"
        @click="()=>{mode = 'single'}"
      >Single Mode</Button>

      <Button
        tool="typing-analyser"
        :secondary="mode !== 'trio'"
        :pokemon="triPokeChoiceTrio"
        @click="()=>{mode = 'trio'}"
      >Trio Mode</Button>
    </div>

    <div v-auto-animate>
      <div v-if="mode == 'single'" class="flex flex-col xl:grid xl:grid-cols-3 gap-8">
        <BaseCard tool="typing-analyser">
          <div class="flex flex-col gap-8">

            <InputGroup v-if="!extraTypesCount" label-key="ts1" tool="typing-analyser">
              <template #label>Types:</template>
              <template #input>
                <TypeSelect v-model="singleTypes[1]" />
                <TypeSelect v-model="singleTypes[2]" :show-none="true" :none-is-any="true" />
              </template>
            </InputGroup>

            <div class="flex flex-col gap-4">

              <template v-if="extraTypesCount">
                <InputGroup v-for="typeIndex in (extraTypesCount+2)" :key="`type-${typeIndex}`" :label-key="`ts${typeIndex}`" tool="typing-analyser">
                  <template #label>Type {{ typeIndex }}:</template>
                  <template #input>
                    <TypeSelect v-model="singleTypes[typeIndex]" class="flex-grow"/>
                    <div class="px-4 py-2 text-tool-typing-analyser hover:text-white cursor-pointer !grow-0" @click="removeExtraType(typeIndex)">x</div>
                  </template>
                </InputGroup>
              </template>

              <div :class="[
                'button-small text-white font-display',
                mode == 'single' ? '!shadow-inner bg-tool-typing-analyser' : 'bg-tool-typing-analyser/75 hover:bg-tool-typing-analyser'
              ]" @click="addExtraType()">
                + Add More Types
              </div>
            </div>

            <div class="flex flex-col gap-4">
              <h4 class="text-lg font-display">Existing: {{ singleGetPokemonOfType.count }}</h4>

              <div class="text-center">
                <PokemonIcon v-for="pokemon in singleGetPokemonOfType.shortlist" :key="pokemon.basename" :pokemon="pokemon"
                  class="inline-block m-0 md:m-2" />
              </div>
            </div>

          </div>

        </BaseCard>
        <BaseCard tool="typing-analyser" class="col-span-2">
          <h2 class='text-2xl font-display mb-8'>Defense Rating: {{singleResults.rating}}</h2>

          <table class="styled-table w-full">
            <thead>
              <tr>
								<th colSpan="2">Defence</th>
								<th>Type</th>
							</tr>
            </thead>
            <tbody>
              <tr
                v-for="(amt_key,i) in singleResults.defenseKeys"
                :key="i"
              >
                <td>
                    {{
                        amt_key === "0"
                        ? "Immune"
                        : (
                            parseFloat(amt_key) < 1
                            ? "Resist"
                            : "Weak"
                        )
                    }}
                </td>
                <td>
                    {{amt_key}}&times;
                </td>
                <td>
                  <TypeBadge
                    v-for="(typeId, j) in singleResults.weaknesses[amt_key]"
                    :key="j"
                    :typeId="typeId"
                    class="m-2"
                  />
                  ({{singleResults.weaknesses[amt_key].length}})
                </td>
              </tr>
            </tbody>
          </table>

        </BaseCard>
      </div>

      <BaseCard v-if="mode == 'trio'" tool="typing-analyser">
        <div class="triangle-box relative">

          <div class="btns absolute top-0 left-0 z-10 flex gap-1">
            <Button
              small
              tool="typing-analyser"
              :icon="RefreshCwIcon"
              @click="setPerfectTrio"
            >Perfect</Button>
            <Button
              small
              secondary
              tool="typing-analyser"
              :icon="RefreshCwIcon"
              @click="setPerfectTrioStarters"
            >Perfect Starters</Button>
          </div>

          <div class="triangle-inner absolute top-20 lg:top-0 left-0 right-0 bottom-0">

              <ResultsLine
                class="r12"
                :clockwise-data="[
                    [trioTypes[1][1], trioResults['p1.t1-p2']],
                    [trioTypes[1][2], trioResults['p1.t2-p2']]
                ]"
                :anti-clockwise-data="[
                    [trioTypes[2][1], trioResults['p2.t1-p1']],
                    [trioTypes[2][2], trioResults['p2.t2-p1']]
                ]"
              />

              <ResultsLine
                class="r23"
                :flipped="true"
                :clockwise-data="[
                    [trioTypes[2][1], trioResults['p2.t1-p3']],
                    [trioTypes[2][2], trioResults['p2.t2-p3']]
                ]"
                :anti-clockwise-data="[
                    [trioTypes[3][1], trioResults['p3.t1-p2']],
                    [trioTypes[3][2], trioResults['p3.t2-p2']]
                ]"
              />

              <ResultsLine
                class="r31"
                :clockwise-data="[
                    [trioTypes[3][1], trioResults['p3.t1-p1']],
                    [trioTypes[3][2], trioResults['p3.t2-p1']]
                ]"
                :anti-clockwise-data="[
                    [trioTypes[1][1], trioResults['p1.t1-p3']],
                    [trioTypes[1][2], trioResults['p1.t2-p3']]
                ]"
              />


              <div class="p p1">
                <div>
                  <InputGroup label-key="tt1" tool="typing-analyser">
                    <template #label>P1 Types:</template>
                    <template #input>
                      <TypeSelect v-model="trioTypes[1][1]" />
                      <TypeSelect v-model="trioTypes[1][2]" :show-none="true" />
                    </template>
                  </InputGroup>

                  <RatingCard :poke-index="1" :trio-results="trioResults"/>
                </div>
              </div>



              <div class="p p2">
                <div>
                  <RatingCard :poke-index="2" :trio-results="trioResults"/>

                  <InputGroup label-key="tt2" tool="typing-analyser">
                    <template #label>P2 Types:</template>
                    <template #input>
                      <TypeSelect v-model="trioTypes[2][1]" />
                      <TypeSelect v-model="trioTypes[2][2]" :show-none="true" />
                    </template>
                  </InputGroup>
                </div>
              </div>

              <div class="p p3">
                <div>
                  <RatingCard :poke-index="3" :trio-results="trioResults"/>

                  <InputGroup label-key="tt3" tool="typing-analyser">
                    <template #label>P3 Types:</template>
                    <template #input>
                      <TypeSelect v-model="trioTypes[3][1]" />
                      <TypeSelect v-model="trioTypes[3][2]" :show-none="true" />
                    </template>
                  </InputGroup>
                </div>
              </div>


              <h4 class="balance font-bold text-2xl text-center w-full">
                <span :class="trioBalanceClass">{{ trioBalanceText }}</span>
              </h4>



          </div>



        </div>
      </BaseCard>
    </div>


  </div>
</template>
